import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../components/ToastContainer';
import Button from '../components/Button';
import Input from '../components/Input';
import Logo from '../components/Logo';
import { Api } from '../api/api';
import usePageTitle from '../utils/usePageTitle';
import getValidTelegram, { isValidTelegramUsername } from '../utils/getValidTelegram';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: var(--color-background);
  position: relative;
  overflow: hidden;
`;

const RegisterCard = styled.div`
  background-color: var(--color-surface);
  border-radius: var(--border-radius-lg);
  padding: 32px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.16);
  position: relative;
  z-index: 1;
`;

const RegisterHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
`;

const RegisterTitle = styled.h1`
  font-family: var(--font-family-main);
  font-size: 30px;
  font-weight: 500;
  color: var(--color-text);
  margin: 0;
`;

const RegisterSubtitle = styled.p`
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const LoginPrompt = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 14px;
  color: var(--color-text-secondary);
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 10px;
  border-radius: var(--border-radius-md);
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.24);
  }
`;

const CheckboxInputWrapper = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
  margin-right: 12px;
`;

const HiddenCheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CustomCheckbox = styled.div<{ $isChecked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid ${props => props.$isChecked ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.24)'};
  background-color: ${props => props.$isChecked ? 'var(--color-primary)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:after {
    content: '';
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    opacity: ${props => props.$isChecked ? 1 : 0};
    transition: opacity 0.2s ease;
    margin-bottom: 2px;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: var(--color-text);
  cursor: pointer;
  user-select: none;
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EyeIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-secondary);
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background-color: var(--color-primary);
  filter: blur(250px);
  opacity: 0.07;
  top: -300px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
`;

const TelegramVerification = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  padding: 20px;
  border-radius: var(--border-radius-md);
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: 16px;
`;

const TelegramVerificationTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: var(--color-text);
  margin: 0;
`;

const TelegramVerificationText = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
`;

export const Register: React.FC = () => {
  usePageTitle('Регистрация');
  
  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    telegram: '',
    password: '',
    confirmPassword: '',
  });
  
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [telegramError, setTelegramError] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Состояния для верификации через Telegram
  const [telegramVerificationLink, setTelegramVerificationLink] = useState<string>('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const { register, isAuthenticated, isLoading, error, clearErrors } = useAuthStore();
  const { showError } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Очистка ошибок при монтировании компонента и при изменении маршрута
  useEffect(() => {
    clearErrors();
  }, [clearErrors, location.pathname]);
  
  // Показываем ошибку через toast когда она появляется
  useEffect(() => {
    if (error) {
      showError(error);
      clearErrors();
    }
  }, [error, showError, clearErrors]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Специальная обработка для поля Telegram
    if (name === 'telegram') {
      // Применяем валидацию и форматирование
      const formattedTelegram = getValidTelegram(value);
      setFormData(prev => ({ ...prev, [name]: formattedTelegram }));
      
      // Проверяем валидность только если поле не пустое
      if (formattedTelegram && !isValidTelegramUsername(formattedTelegram)) {
        setTelegramError('Username должен содержать от 5 до 32 символов: буквы, цифры и подчеркивания');
      } else {
        setTelegramError('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (error) clearErrors();
    
    // Проверка совпадения паролей
    if (name === 'confirmPassword' || name === 'password') {
      const password = name === 'password' ? value : formData.password;
      const confirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword;
      setPasswordMismatch(password !== confirmPassword && confirmPassword.length > 0);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setPasswordMismatch(true);
      showError('Пароли не совпадают');
      return;
    }
    
    if (formData.telegram && telegramError) {
      showError(telegramError);
      return;
    }
    
    if (!termsAccepted) {
      showError('Необходимо принять условия использования');
      return;
    }
    
    try {
      const api = new Api();
      const response = await api.signUp({
        user_name: formData.nickname,
        email: formData.email,
        telegram: formData.telegram,
        password: formData.password
      });
      
      // Проверяем, вернул ли сервер ссылку на верификацию через Telegram
      if (response.link) {
        setTelegramVerificationLink(response.link);
        setRegistrationSuccess(true);
      } else {
        // Если нет ссылки, значит верификация не требуется, продолжаем стандартный процесс регистрации
        await register(formData.email, formData.password, formData.nickname, formData.telegram);
      }
    } catch (error: any) {
      // Ошибки будут показаны через useEffect выше
      
    }
  };
  
  const handleCheckboxClick = () => {
    setTermsAccepted(!termsAccepted);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const renderEyeIcon = (show: boolean, onClick: () => void) => (
    <EyeIcon onClick={onClick}>
      {show ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
      )}
    </EyeIcon>
  );
  
  // Если пользователь уже авторизован, перенаправляем на дашборд
  if (isAuthenticated) {
    return <Navigate to="/statistics" replace />;
  }
  
  if (registrationSuccess) {
    return (
      <RegisterContainer>
        <BackgroundGlow />
        
        <RegisterCard>
          <RegisterHeader>
            <Logo size="medium" />
            <div>
              <RegisterTitle>Подтверждение регистрации</RegisterTitle>
              <RegisterSubtitle>Завершите процесс регистрации через Telegram</RegisterSubtitle>
            </div>
          </RegisterHeader>
          
          <TelegramVerification>
            <TelegramVerificationText>
              Для завершения регистрации перейдите в Telegram и отправьте боту сообщение для подтверждения вашей учетной записи.
            </TelegramVerificationText>
            
            <Button 
              variant="primary" 
              fullWidth
              onClick={() => window.open(`https://${telegramVerificationLink}`, '_blank', 'noopener,noreferrer')}
            >
              Перейти в Telegram
            </Button>
            
            <Button
              variant="secondary"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Вернуться на страницу входа
            </Button>
            
            <LoginPrompt>
              Уже подтвердили? <Link to="/login">Войти в систему</Link>
            </LoginPrompt>
          </TelegramVerification>
        </RegisterCard>
      </RegisterContainer>
    );
  }
  
  return (
    <RegisterContainer>
      <BackgroundGlow />
      
      <RegisterCard>
        <RegisterHeader>
          <Logo size="medium" />
          <div>
            <RegisterTitle>Регистрация</RegisterTitle>
            <RegisterSubtitle>Создайте аккаунт для доступа к личному кабинету</RegisterSubtitle>
          </div>
        </RegisterHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Input
              type="text"
              name="nickname"
              label="Никнейм"
              placeholder="Никнейм"
              value={formData.nickname}
              onChange={handleChange}
              fullWidth
              required
            />
            
            <Input
              type="email"
              name="email"
              label="e-Mail"
              placeholder="e-Mail"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
            />
            
            <Input
              type="text"
              name="telegram"
              label="Telegram"
              placeholder="@username"
              value={formData.telegram}
              onChange={handleChange}
              error={telegramError}
              fullWidth
            />
            
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              label="Введите пароль"
              placeholder="Пароль"
              value={formData.password}
              onChange={handleChange}
              icon={renderEyeIcon(showPassword, togglePasswordVisibility)}
              iconPosition="right"
              onIconClick={togglePasswordVisibility}
              fullWidth
              required
            />
            
            <Input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              label="Подтвердите пароль"
              placeholder="Подтвердите пароль"
              value={formData.confirmPassword}
              onChange={handleChange}
              icon={renderEyeIcon(showConfirmPassword, toggleConfirmPasswordVisibility)}
              iconPosition="right"
              onIconClick={toggleConfirmPasswordVisibility}
              error={passwordMismatch ? "Пароли не совпадают" : undefined}
              fullWidth
              required
            />
          </FormField>

          <CheckboxContainer onClick={handleCheckboxClick}>
            <CheckboxInputWrapper>
              <HiddenCheckboxInput
                type="checkbox" 
                id="terms"
                checked={termsAccepted}
                onChange={() => {}}
              />
              <CustomCheckbox $isChecked={termsAccepted} />
            </CheckboxInputWrapper>
            <CheckboxLabel htmlFor="terms">
              Я принимаю <Link to="https://legitfam.pro/docs/terms-of-use.html">условия использования</Link> и <Link to="https://legitfam.pro/docs/privacy-policy.html">политику конфиденциальности</Link>
            </CheckboxLabel>
          </CheckboxContainer>
          
          <FormActions>
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth
              loading={isLoading}
              disabled={!termsAccepted || passwordMismatch || !!telegramError}
            >
              Регистрация
            </Button>
            
            <LoginPrompt>
              Уже есть аккаунт? <Link to="/login">Войти</Link>
            </LoginPrompt>
          </FormActions>
        </Form>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 